import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, FolderOpen, ArrowRight, ClipboardList, GripVertical, Minus, Check, FileText } from "lucide-react";
import { MAX_PLAN_ACTIVITIES, PLAN_ACTIVITY_LIMIT_MESSAGE } from "@/lib/planLayout";

// Compact list editor for session-level principles. Mirrors the coaching
// point add-then-confirm flow so users get a consistent interaction across
// the app. Items are plain strings.
const PrinciplesList = ({ items, onChange, accent = "emerald", testIdPrefix }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const confirmAdd = () => {
    const text = draft.trim();
    if (!text) return;
    onChange([...(items || []), text]);
    setDraft("");
    setAdding(false);
  };
  const cancelAdd = () => {
    setDraft("");
    setAdding(false);
  };
  const removeAt = (i) => {
    onChange((items || []).filter((_, idx) => idx !== i));
  };
  return (
    <div className="flex flex-col gap-2" data-testid={testIdPrefix}>
      {(items || []).length > 0 && (
        <ul className="flex flex-col gap-1">
          {(items || []).map((text, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-xs text-slate-700 leading-snug"
              data-testid={`${testIdPrefix}-row-${i}`}
            >
              <span
                aria-hidden
                className={"mt-1.5 w-1 h-1 rounded-full shrink-0 " + (accent === "sky" ? "bg-sky-500" : "bg-emerald-500")}
              />
              <span className="flex-1 break-words">{text}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testIdPrefix}-remove-${i}`}
                aria-label="Remove principle"
                className="w-5 h-5 shrink-0 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-form`}>
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); confirmAdd(); }
              else if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
            }}
            placeholder={accent === "sky" ? "e.g. Compact defensive block" : "e.g. Attack behind the line"}
            data-testid={`${testIdPrefix}-input`}
            className="h-8"
          />
          <button
            type="button"
            onClick={confirmAdd}
            disabled={!draft.trim()}
            data-testid={`${testIdPrefix}-confirm`}
            aria-label="Add"
            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={cancelAdd}
            data-testid={`${testIdPrefix}-cancel`}
            className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid={`${testIdPrefix}-add`}
          className="self-start flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-900 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add principle
        </button>
      )}
    </div>
  );
};

// Session-level overview + entry point into individual Activities.
// The heavy Activity Editor keeps rendering underneath — this view is a
// lightweight roll-up above it. Activity rows are drag-and-droppable so
// coaches can reorder the flow without opening the editor.
export const SessionBuilder = ({
  workspaceTabs,
  activeTabId,
  liveActivity,
  sessionName,
  onSessionNameChange,
  sessionMeta,
  onSessionMetaChange,
  onOpenActivity,
  onNewBlankActivity,
  onAddFromLibrary,
  onReorder,
  onGeneratePlan,
  computeTotalMinutes,
}) => {
  const meta = sessionMeta || {};
  const setMeta = (patch) =>
    onSessionMetaChange && onSessionMetaChange({ ...meta, ...patch });
  const activities = workspaceTabs.map((t, i) => {
    const activityMeta = t.tabId === activeTabId ? liveActivity : t.activity;
    return {
      tabId: t.tabId,
      index: i + 1,
      name: (activityMeta?.name || "").trim() || `Activity ${i + 1}`,
      total: computeTotalMinutes(activityMeta || {}),
      isActive: t.tabId === activeTabId,
    };
  });
  const sessionTotal = activities.reduce((sum, a) => sum + (a.total || 0), 0);

  // Native HTML5 drag-and-drop state. `draggingTabId` marks the row that
  // is currently being dragged; `dropTarget` marks where the drop indicator
  // should render (tabId + 'above' | 'below').
  const [draggingTabId, setDraggingTabId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragOver = (e, tabId) => {
    if (!draggingTabId || draggingTabId === tabId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const above = e.clientY < rect.top + rect.height / 2;
    const nextPos = above ? "above" : "below";
    if (!dropTarget || dropTarget.tabId !== tabId || dropTarget.position !== nextPos) {
      setDropTarget({ tabId, position: nextPos });
    }
  };
  const handleDrop = (e, tabId) => {
    e.preventDefault();
    if (!draggingTabId || draggingTabId === tabId) {
      setDraggingTabId(null);
      setDropTarget(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const above = e.clientY < rect.top + rect.height / 2;
    onReorder && onReorder(draggingTabId, tabId, above ? "above" : "below");
    setDraggingTabId(null);
    setDropTarget(null);
  };
  const handleDragEnd = () => {
    setDraggingTabId(null);
    setDropTarget(null);
  };

  return (
    <div
      className="flex-1 min-h-0 overflow-y-auto bg-slate-50"
      data-testid="session-builder"
    >
      <div className="max-w-4xl mx-auto px-8 py-10 flex flex-col gap-8">
        <header className="flex items-end justify-between gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-slate-400">
              Session
            </span>
            <Input
              value={sessionName}
              onChange={(e) => onSessionNameChange(e.target.value)}
              placeholder="Untitled Session"
              data-testid="session-name-input"
              className="text-2xl font-bold border-none bg-transparent shadow-none px-0 h-auto py-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-slate-300"
              style={{ fontFamily: "Manrope, sans-serif" }}
            />
          </div>
          <div
            className="shrink-0 rounded-lg border border-slate-200 bg-white px-5 py-3 flex flex-col items-end"
            data-testid="session-total"
          >
            <span
              className="text-3xl font-black text-slate-900 leading-none tabular-nums"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="session-total-value"
            >
              {sessionTotal}
            </span>
            <span className="text-[9px] tracking-[0.22em] uppercase font-bold text-slate-400 mt-1">
              Total min
            </span>
          </div>
          <button
            type="button"
            onClick={onGeneratePlan}
            disabled={activities.length === 0 || activities.length > MAX_PLAN_ACTIVITIES}
            title={activities.length > MAX_PLAN_ACTIVITIES ? PLAN_ACTIVITY_LIMIT_MESSAGE : undefined}
            data-testid="generate-plan-btn"
            className="shrink-0 self-stretch flex items-center gap-2 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FileText className="w-4 h-4" />
            Generate Plan
          </button>
        </header>

        {activities.length > MAX_PLAN_ACTIVITIES && (
          <p
            className="-mt-6 text-right text-xs font-medium text-amber-700"
            data-testid="plan-activity-limit-message"
            role="status"
          >
            {PLAN_ACTIVITY_LIMIT_MESSAGE}
          </p>
        )}

        <section
          className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col gap-4"
          data-testid="session-details"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-500">
              Session Details
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="session-date" className="text-[11px] font-semibold text-slate-600">Date</Label>
              <Input
                id="session-date"
                type="date"
                value={meta.date || ""}
                onChange={(e) => setMeta({ date: e.target.value })}
                data-testid="session-date-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="session-start" className="text-[11px] font-semibold text-slate-600">Start Time</Label>
              <Input
                id="session-start"
                type="time"
                value={meta.startTime || ""}
                onChange={(e) => setMeta({ startTime: e.target.value })}
                data-testid="session-starttime-input"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="session-location" className="text-[11px] font-semibold text-slate-600">Location</Label>
            <Input
              id="session-location"
              value={meta.location || ""}
              onChange={(e) => setMeta({ location: e.target.value })}
              placeholder="e.g. Main pitch, north field"
              data-testid="session-location-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="session-objective" className="text-[11px] font-semibold text-slate-600">Objective</Label>
            <Textarea
              id="session-objective"
              value={meta.objective || ""}
              onChange={(e) => setMeta({ objective: e.target.value })}
              placeholder="What is the theme of the day?"
              rows={2}
              data-testid="session-objective-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] font-semibold text-slate-600">Load</Label>
            <Select
              value={meta.load || ""}
              onValueChange={(v) => setMeta({ load: v })}
            >
              <SelectTrigger data-testid="session-load-select" className="w-full">
                <SelectValue placeholder="Choose load" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low" data-testid="session-load-low">Low</SelectItem>
                <SelectItem value="medium" data-testid="session-load-medium">Medium</SelectItem>
                <SelectItem value="high" data-testid="session-load-high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-1">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700">
                Offensive Principles
              </span>
              <PrinciplesList
                items={meta.offensivePrinciples || []}
                onChange={(next) => setMeta({ offensivePrinciples: next })}
                accent="emerald"
                testIdPrefix="session-offensive"
              />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-sky-700">
                Defensive Principles
              </span>
              <PrinciplesList
                items={meta.defensivePrinciples || []}
                onChange={(next) => setMeta({ defensivePrinciples: next })}
                accent="sky"
                testIdPrefix="session-defensive"
              />
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-2" data-testid="session-builder-activities">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-500">
              Activities · {activities.length}
            </span>
          </div>

          {activities.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
              This session has no activities yet.
            </div>
          ) : (
            <ul
              className="flex flex-col gap-2"
              onDragLeave={(e) => {
                // Only clear if we're leaving the list entirely.
                if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(null);
              }}
            >
              {activities.map((a) => {
                const showAbove =
                  dropTarget && dropTarget.tabId === a.tabId && dropTarget.position === "above";
                const showBelow =
                  dropTarget && dropTarget.tabId === a.tabId && dropTarget.position === "below";
                const isDragging = draggingTabId === a.tabId;
                return (
                  <li key={a.tabId} className="relative">
                    {showAbove && (
                      <span
                        className="absolute left-0 right-0 -top-1 h-0.5 rounded-full bg-slate-900"
                        data-testid="drop-indicator"
                        aria-hidden
                      />
                    )}
                    <div
                      draggable
                      onDragStart={(e) => {
                        setDraggingTabId(a.tabId);
                        try {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", a.tabId);
                        } catch {}
                      }}
                      onDragOver={(e) => handleDragOver(e, a.tabId)}
                      onDrop={(e) => handleDrop(e, a.tabId)}
                      onDragEnd={handleDragEnd}
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenActivity(a.tabId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpenActivity(a.tabId);
                        }
                      }}
                      data-testid={`session-activity-row-${a.tabId}`}
                      data-active={a.isActive ? "true" : "false"}
                      className={
                        "w-full group flex items-center gap-3 rounded-lg border bg-white pl-2 pr-4 py-3 hover:border-slate-300 hover:shadow-sm transition-all text-left cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 " +
                        (isDragging
                          ? "opacity-40 border-slate-300"
                          : "border-slate-200")
                      }
                    >
                      <span
                        data-testid={`session-activity-handle-${a.tabId}`}
                        aria-hidden
                        className="shrink-0 w-5 h-5 flex items-center justify-center text-slate-300 group-hover:text-slate-500 transition-colors"
                        title="Drag to reorder"
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                      <span
                        className="shrink-0 w-9 h-9 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-sm font-bold tabular-nums"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {a.index}
                      </span>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {a.name}
                        </span>
                        <span className="text-xs text-slate-500 tabular-nums">
                          {a.total} min
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenActivity(a.tabId);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        data-testid={`session-activity-open-${a.tabId}`}
                        className="shrink-0 flex items-center gap-1 h-8 px-3 rounded-md text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-900 hover:text-white transition-colors"
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                    {showBelow && (
                      <span
                        className="absolute left-0 right-0 -bottom-1 h-0.5 rounded-full bg-slate-900"
                        data-testid="drop-indicator"
                        aria-hidden
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="grid grid-cols-2 gap-3" data-testid="session-builder-add">
          <button
            type="button"
            onClick={onNewBlankActivity}
            data-testid="session-builder-new-activity"
            className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New blank activity
          </button>
          <button
            type="button"
            onClick={onAddFromLibrary}
            data-testid="session-builder-add-from-library"
            className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-colors"
          >
            <FolderOpen className="w-4 h-4" />
            Add from Activity Library
          </button>
        </section>

        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-2">
          <ClipboardList className="w-3.5 h-3.5" />
          Tip: drag the grip handle to reorder or click Open to edit an activity in the Activity Editor.
        </p>
      </div>
    </div>
  );
};
